import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'

import {
  getIsInitializingSession,
  getIsRecording,
  getIsSummarizing,
} from '~/store/duck/audioRecorder'
import { getAppointmentId } from '~/store/reducers/soap'

import { useAudioRecorderMetadata } from './useAudioRecorderMetadata'
import { useAudioRecorderSession } from './useAudioRecorderSession'

// Discriminated union ensures disabledReason is always present when disabled
export type AudioRecorderDisabledState =
  | { disabled: true; disabledReason: string }
  | { disabled: false }

export const useAudioRecorderDisabled = (): AudioRecorderDisabledState => {
  const { t } = useTranslation('Common')
  const { pathname } = useLocation()
  const appointmentId = useSelector(getAppointmentId)
  const isRecording = useSelector(getIsRecording)
  const isSummarizing = useSelector(getIsSummarizing)
  const isInitializingSession = useSelector(getIsInitializingSession)
  const session = useAudioRecorderSession()
  useAudioRecorderMetadata() // Initialize metadata

  // Always enabled during active recording or summarization
  if (isRecording || isSummarizing) {
    return { disabled: false }
  }

  // Disable if not on SOAP page
  const isOnSoapPage = pathname.includes('/soap/')
  if (!isOnSoapPage) {
    return {
      disabled: true,
      disabledReason: t('Common:AUDIO_RECORDER.OPEN_SOAP_TO_USE'),
    }
  }

  // On SOAP page but appointment data not loaded yet, or session initializing
  const hasValidCorrelationId = Boolean(session.correlationId)
  const shouldSkipInitialization = !hasValidCorrelationId
  // Temporarily disabled - waiting on backend support for medical history
  // || !isMedicalHistoryReady

  if (!appointmentId || shouldSkipInitialization || isInitializingSession) {
    return {
      disabled: true,
      disabledReason: t('Common:INITIALIZING'),
    }
  }

  // Enable when all conditions are met
  return { disabled: false }
}
